import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Copy, Eye, EyeOff, GripVertical, ChevronDown, ChevronUp,
  Save, X, Star, Users, DollarSign, CheckCircle2, Archive, RotateCcw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAutoDraft } from '../../hooks/useAutoDraft';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPlan = {
  name: '', name_es: '', short_description: '', short_description_es: '',
  price: '', price_note: '', validity_text: '', benefit_categories: [],
  best_for: '', investment_note: '', reps_included: 1,
  is_active: true, is_public: true, is_portal_visible: true, is_featured: false,
  display_order: 0, display_priority: 0, color: '#002249', icon: '',
  action_type: 'portal_flow', action_url: '', action_label: '',
};

const MembershipPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  // Auto-draft for the editor form — scoped per plan id (or "new")
  const draftKey = editingPlan
    ? `dccf_draft_plan_${editingPlan.id || 'new'}`
    : null;
  const { restored, clearDraft } = useAutoDraft(draftKey, editingPlan, {
    enabled: !!editingPlan,
  });
  const dirty = useMemo(() => {
    if (!editingPlan || !initialSnapshot) return false;
    try { return JSON.stringify(editingPlan) !== JSON.stringify(initialSnapshot); } catch { return false; }
  }, [editingPlan, initialSnapshot]);
  useUnsavedChanges(dirty);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/membership-plans/all`, { withCredentials: true });
      setPlans(res.data);
    } catch { /* */ } finally { setLoading(false); }
  };

  const openEditor = (plan) => {
    setEditingPlan(plan);
    setInitialSnapshot(plan);
  };

  const closeEditor = () => {
    // Draft is preserved — user can restore it via the banner on re-open, or clear it explicitly.
    setEditingPlan(null);
    setInitialSnapshot(null);
  };

  const restoreDraft = () => {
    if (restored?.data) {
      setEditingPlan(restored.data);
      toast.success('Draft restored');
    }
  };

  const handleSave = async () => {
    if (!editingPlan.name.trim()) { toast.error('Plan name is required'); return; }
    setSaving(true);
    try {
      if (editingPlan.id) {
        await axios.put(`${API}/admin/membership-plans/${editingPlan.id}`, editingPlan, { withCredentials: true });
        toast.success('Plan updated successfully');
      } else {
        await axios.post(`${API}/admin/membership-plans`, editingPlan, { withCredentials: true });
        toast.success('Plan created successfully');
      }
      clearDraft();
      setEditingPlan(null);
      setInitialSnapshot(null);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save — your draft is preserved, try again.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/membership-plans/${id}`, { withCredentials: true });
      toast.success('Plan deleted');
      fetchPlans();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/admin/membership-plans/${id}/duplicate`, {}, { withCredentials: true });
      toast.success('Plan duplicated');
      fetchPlans();
    } catch (err) { toast.error('Failed to duplicate'); }
  };

  const handleToggle = async (plan, field) => {
    try {
      const updated = { ...plan, [field]: !plan[field] };
      await axios.put(`${API}/admin/membership-plans/${plan.id}`, updated, { withCredentials: true });
      fetchPlans();
    } catch (err) { toast.error('Failed to update'); }
  };

  const addCategory = () => {
    setEditingPlan(prev => ({
      ...prev,
      benefit_categories: [...prev.benefit_categories, { category_name: '', items: [''] }],
    }));
  };

  const removeCategory = (idx) => {
    setEditingPlan(prev => ({
      ...prev,
      benefit_categories: prev.benefit_categories.filter((_, i) => i !== idx),
    }));
  };

  const updateCategory = (idx, field, value) => {
    setEditingPlan(prev => {
      const cats = [...prev.benefit_categories];
      cats[idx] = { ...cats[idx], [field]: value };
      return { ...prev, benefit_categories: cats };
    });
  };

  const addItem = (catIdx) => {
    setEditingPlan(prev => {
      const cats = [...prev.benefit_categories];
      cats[catIdx] = { ...cats[catIdx], items: [...cats[catIdx].items, ''] };
      return { ...prev, benefit_categories: cats };
    });
  };

  const updateItem = (catIdx, itemIdx, value) => {
    setEditingPlan(prev => {
      const cats = [...prev.benefit_categories];
      const items = [...cats[catIdx].items];
      items[itemIdx] = value;
      cats[catIdx] = { ...cats[catIdx], items };
      return { ...prev, benefit_categories: cats };
    });
  };

  const removeItem = (catIdx, itemIdx) => {
    setEditingPlan(prev => {
      const cats = [...prev.benefit_categories];
      cats[catIdx] = { ...cats[catIdx], items: cats[catIdx].items.filter((_, i) => i !== itemIdx) };
      return { ...prev, benefit_categories: cats };
    });
  };

  const inputClass = "w-full border border-slate-200 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]";

  // ===== EDIT FORM =====
  if (editingPlan) {
    const hasDraft = restored?.data && restored.data.id === editingPlan.id &&
      JSON.stringify(restored.data) !== JSON.stringify(editingPlan);
    return (
      <div className="p-6 lg:p-8" data-testid="plan-editor">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#002249]">{editingPlan.id ? 'Edit Plan' : 'New Plan'}</h1>
          <div className="flex items-center gap-2">
            {dirty && <span className="text-[11px] font-medium text-amber-600" data-testid="plan-dirty-badge">Unsaved changes</span>}
            <Button variant="outline" onClick={closeEditor} className="rounded-sm" data-testid="cancel-edit"><X className="w-4 h-4 mr-1" />Cancel</Button>
          </div>
        </div>
        {hasDraft && (
          <div
            className="max-w-3xl mb-4 p-3 rounded-lg bg-[#002249]/5 border border-[#002249]/15 flex items-center justify-between gap-3"
            data-testid="plan-draft-restore-banner"
          >
            <span className="text-[13px] text-[#002249] font-medium flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> A draft of this plan was auto-saved.
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={clearDraft} className="h-8" data-testid="plan-discard-draft">Discard</Button>
              <Button size="sm" onClick={restoreDraft} className="h-8 bg-[#002249]" data-testid="plan-restore-draft">Restore</Button>
            </div>
          </div>
        )}
        <div className="max-w-3xl space-y-6">
          {/* Basic Info */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="font-bold text-[#002249] mb-4">Basic Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Plan Name (English) *</label><input value={editingPlan.name} onChange={e => setEditingPlan({...editingPlan, name: e.target.value})} className={inputClass} data-testid="plan-name" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Plan Name (Spanish)</label><input value={editingPlan.name_es || ''} onChange={e => setEditingPlan({...editingPlan, name_es: e.target.value})} className={inputClass} data-testid="plan-name-es" /></div>
              <div className="md:col-span-2"><label className="block text-xs font-medium text-slate-600 mb-1">Short Description</label><textarea value={editingPlan.short_description} onChange={e => setEditingPlan({...editingPlan, short_description: e.target.value})} className={`${inputClass} min-h-[60px]`} data-testid="plan-desc" /></div>
              <div className="md:col-span-2"><label className="block text-xs font-medium text-slate-600 mb-1">Short Description (Spanish)</label><textarea value={editingPlan.short_description_es || ''} onChange={e => setEditingPlan({...editingPlan, short_description_es: e.target.value})} className={`${inputClass} min-h-[60px]`} /></div>
            </div>
          </div>
          {/* Pricing */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="font-bold text-[#002249] mb-4">Pricing & Investment</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Price (display text)</label><input value={editingPlan.price || ''} onChange={e => setEditingPlan({...editingPlan, price: e.target.value})} placeholder="$300/year" className={inputClass} data-testid="plan-price" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Price Note</label><input value={editingPlan.price_note || ''} onChange={e => setEditingPlan({...editingPlan, price_note: e.target.value})} placeholder="Founding member rate" className={inputClass} /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Validity Text</label><input value={editingPlan.validity_text || ''} onChange={e => setEditingPlan({...editingPlan, validity_text: e.target.value})} placeholder="Valid through Dec 2026" className={inputClass} /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Investment Note</label><input value={editingPlan.investment_note || ''} onChange={e => setEditingPlan({...editingPlan, investment_note: e.target.value})} className={inputClass} /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Business Reps Included</label><input type="number" min="1" value={editingPlan.reps_included} onChange={e => setEditingPlan({...editingPlan, reps_included: parseInt(e.target.value) || 1})} className={inputClass} data-testid="plan-reps" /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Best For</label><input value={editingPlan.best_for || ''} onChange={e => setEditingPlan({...editingPlan, best_for: e.target.value})} className={inputClass} /></div>
            </div>
          </div>
          {/* Benefit Categories */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[#002249]">Benefit Categories</h2>
              <Button size="sm" variant="outline" onClick={addCategory} className="rounded-sm" data-testid="add-category"><Plus className="w-3.5 h-3.5 mr-1" />Add Category</Button>
            </div>
            <div className="space-y-4">
              {editingPlan.benefit_categories.map((cat, ci) => (
                <div key={ci} className="border border-slate-200 rounded-sm p-4 bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <input value={cat.category_name} onChange={e => updateCategory(ci, 'category_name', e.target.value)} placeholder="Category name..." className="border border-slate-200 rounded-sm p-2 text-sm font-semibold flex-1 mr-2 focus:outline-none focus:ring-1 focus:ring-[#002249]/20" data-testid={`cat-name-${ci}`} />
                    <button onClick={() => removeCategory(ci)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-1.5 ml-2">
                    {cat.items.map((item, ii) => (
                      <div key={ii} className="flex items-center space-x-2">
                        <span className="text-green-500 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /></span>
                        <input value={item} onChange={e => updateItem(ci, ii, e.target.value)} placeholder="Benefit item..." className="flex-1 border border-slate-200 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#002249]/20" data-testid={`item-${ci}-${ii}`} />
                        <button onClick={() => removeItem(ci, ii)} className="text-slate-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    <button onClick={() => addItem(ci)} className="text-xs text-[#002249] hover:underline mt-1 flex items-center"><Plus className="w-3 h-3 mr-0.5" />Add benefit</button>
                  </div>
                </div>
              ))}
              {editingPlan.benefit_categories.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No categories yet. Click "Add Category" above.</p>}
            </div>
          </div>
          {/* Action / Payment Flow — dynamic CTA routing (Admin-controlled, no hardcoding) */}
          <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="action-flow-card">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="font-bold text-[#002249]">Action / Payment Flow</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Where should the plan's button send users? Admin decides — switchable anytime.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Action Type</label>
                <select
                  value={editingPlan.action_type || 'portal_flow'}
                  onChange={e => setEditingPlan({ ...editingPlan, action_type: e.target.value })}
                  className={inputClass}
                  data-testid="action-type-select"
                >
                  <option value="portal_flow">Portal Flow (default — opens /member-login)</option>
                  <option value="external_link">External Link (Zeffy, PayPal, Square, etc.)</option>
                  <option value="custom_internal_page">Custom Internal Page (e.g. /membership/apply)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Action URL
                  {(editingPlan.action_type || 'portal_flow') === 'portal_flow' && (
                    <span className="text-slate-400 font-normal"> — not used for portal flow</span>
                  )}
                </label>
                <input
                  value={editingPlan.action_url || ''}
                  onChange={e => setEditingPlan({ ...editingPlan, action_url: e.target.value })}
                  placeholder={
                    (editingPlan.action_type || 'portal_flow') === 'external_link' ? 'https://zeffy.com/xxxx' :
                    (editingPlan.action_type || 'portal_flow') === 'custom_internal_page' ? '/membership/apply' :
                    'Not required for Portal Flow'
                  }
                  disabled={(editingPlan.action_type || 'portal_flow') === 'portal_flow'}
                  className={`${inputClass} ${(editingPlan.action_type || 'portal_flow') === 'portal_flow' ? 'bg-slate-50 text-slate-400' : ''}`}
                  data-testid="action-url-input"
                />
                {(editingPlan.action_type || 'portal_flow') === 'external_link' && (
                  <p className="text-[10px] text-slate-500 mt-1">Must start with <code className="bg-slate-100 px-1 font-mono">https://</code>. Opens in new tab.</p>
                )}
                {(editingPlan.action_type || 'portal_flow') === 'custom_internal_page' && (
                  <p className="text-[10px] text-slate-500 mt-1">Must start with <code className="bg-slate-100 px-1 font-mono">/</code>. Uses React Router.</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Button Label <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  value={editingPlan.action_label || ''}
                  onChange={e => setEditingPlan({ ...editingPlan, action_label: e.target.value })}
                  placeholder="Apply for Membership · Pay via Zeffy · Join Now"
                  className={inputClass}
                  data-testid="action-label-input"
                />
              </div>
            </div>
          </div>

          {/* Display Settings */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="font-bold text-[#002249] mb-4">Display & Visibility</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Display Order</label><input type="number" value={editingPlan.display_order} onChange={e => setEditingPlan({...editingPlan, display_order: parseInt(e.target.value) || 0})} className={inputClass} /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Display Priority (0=top)</label><input type="number" value={editingPlan.display_priority} onChange={e => setEditingPlan({...editingPlan, display_priority: parseInt(e.target.value) || 0})} className={inputClass} /></div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Color</label><div className="flex items-center space-x-2"><input type="color" value={editingPlan.color || '#002249'} onChange={e => setEditingPlan({...editingPlan, color: e.target.value})} className="w-10 h-10 border border-slate-200 rounded cursor-pointer" /><input value={editingPlan.color || ''} onChange={e => setEditingPlan({...editingPlan, color: e.target.value})} className={`${inputClass} flex-1`} /></div></div>
            </div>
            <div className="flex flex-wrap gap-4 mt-4">
              {[{key:'is_active',label:'Active'},{key:'is_public',label:'Public Website'},{key:'is_portal_visible',label:'Member Portal'},{key:'is_featured',label:'Featured'}].map(t => (
                <label key={t.key} className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={editingPlan[t.key]} onChange={e => setEditingPlan({...editingPlan, [t.key]: e.target.checked})} className="w-4 h-4 rounded border-slate-300" data-testid={`toggle-${t.key}`} />
                  <span className="text-sm text-slate-700">{t.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setEditingPlan(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="save-plan"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Plan'}</Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-membership-plans">
      {/* Page header — matches other admin pages */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#002249]">Membership Plans</h1>
          <p className="text-[13px] text-slate-500 mt-1">{plans.length} plan{plans.length !== 1 ? 's' : ''} · Manage pricing, benefits, and CTA routing</p>
        </div>
        <Button onClick={() => openEditor({...emptyPlan, display_order: plans.length + 1})} size="sm" className="bg-[#002249] hover:bg-[#001630] rounded-sm h-10 w-full sm:w-auto" data-testid="create-plan-btn">
          <Plus className="w-4 h-4 mr-1.5" />New Plan
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-sm border border-dashed border-slate-200">
          <DollarSign className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-medium text-slate-500">No membership plans yet</p>
          <p className="text-xs text-slate-400 mt-1">Create your first plan to start onboarding members.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map((plan, index) => {
            const isExpanded = expandedId === plan.id;
            const planColor = plan.color || '#002249';
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * index }}
                className={`bg-white border rounded-sm overflow-hidden transition-colors hover:border-slate-300 ${plan.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
                data-testid={`plan-card-${index}`}
              >
                {/* ─── 3-ZONE GRID — matches the PRD spec ─────────────────
                    Desktop: [color bar] [title+desc] ·  [metadata] ·  [actions]
                    Mobile : stacks vertically — title+desc / metadata / actions
                ───────────────────────────────────────────────────────── */}
                <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-4 sm:gap-6 items-center">
                  {/* LEFT ZONE — identity */}
                  <div className="flex items-stretch gap-3 min-w-0">
                    <div className="w-1 rounded-full flex-shrink-0 self-stretch" style={{ backgroundColor: planColor }} />
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-[15px] font-semibold text-[#002249] leading-tight">{plan.name}</h3>
                        {plan.is_featured && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />}
                        {!plan.is_active && <span className="text-[10px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Inactive</span>}
                        {plan.action_type === 'external_link' && plan.action_url && (
                          <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title={plan.action_url} data-testid={`action-badge-${index}`}>EXTERNAL</span>
                        )}
                        {plan.action_type === 'custom_internal_page' && plan.action_url && (
                          <span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded" title={plan.action_url} data-testid={`action-badge-${index}`}>INTERNAL</span>
                        )}
                      </div>
                      <p className="text-[13px] text-slate-500 line-clamp-1 leading-snug">{plan.short_description || <span className="italic text-slate-300">No description</span>}</p>
                    </div>
                  </div>

                  {/* CENTER ZONE — metadata. Perfectly left-aligned on mobile (with border-top separator),
                      horizontal row on desktop. 8-col rhythm; text-slate-500 secondary tone. */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-500 sm:justify-end sm:min-w-[280px] pt-3 sm:pt-0 border-t border-slate-100 sm:border-t-0">
                    <span className="font-semibold text-[#002249] text-[13px]" data-testid={`plan-price-${index}`}>{plan.price || '—'}</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{plan.reps_included} rep{plan.reps_included !== 1 ? 's' : ''}</span>
                    <span className="inline-flex items-center gap-1">
                      {plan.is_public
                        ? <><Eye className="w-3 h-3 text-emerald-500" /><span>Public</span></>
                        : <><EyeOff className="w-3 h-3 text-slate-400" /><span>Hidden</span></>}
                    </span>
                    <span className="text-slate-400">Order {plan.display_order}</span>
                  </div>

                  {/* RIGHT ZONE — actions. Grouped, equal 4px gaps, subtle default + colored hover states. */}
                  <div className="flex items-center gap-0.5 flex-shrink-0 border-t border-slate-100 sm:border-t-0 pt-3 sm:pt-0 -mx-1 sm:mx-0">
                    <Button size="icon" variant="ghost" onClick={() => openEditor({...plan})} title="Edit" className="h-9 w-9 text-slate-400 hover:text-[#002249]" data-testid={`edit-plan-${index}`}><Edit2 className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDuplicate(plan.id)} title="Duplicate" className="h-9 w-9 text-slate-400 hover:text-[#002249]" data-testid={`dup-plan-${index}`}><Copy className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => handleToggle(plan, 'is_active')} title={plan.is_active ? 'Archive' : 'Activate'} className={`h-9 w-9 ${plan.is_active ? 'text-slate-400 hover:text-amber-500' : 'text-emerald-500 hover:text-emerald-600'}`} data-testid={`toggle-active-${index}`}>{plan.is_active ? <Archive className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}</Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(plan.id)} title="Delete" className="h-9 w-9 text-slate-400 hover:text-rose-500" data-testid={`delete-plan-${index}`}><Trash2 className="w-4 h-4" /></Button>
                    <div className="w-px h-5 bg-slate-200 mx-1" />
                    <Button size="icon" variant="ghost" onClick={() => setExpandedId(isExpanded ? null : plan.id)} title={isExpanded ? 'Collapse' : 'Expand'} className="h-9 w-9 text-slate-400 hover:text-[#002249]" data-testid={`expand-plan-${index}`}>{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-200 p-4 sm:p-5 bg-slate-50/60 text-[13px] space-y-2.5">
                    {plan.name_es && <p><span className="font-semibold text-slate-600">Spanish:</span> <span className="text-slate-600">{plan.name_es}</span></p>}
                    {plan.validity_text && <p><span className="font-semibold text-slate-600">Validity:</span> <span className="text-slate-600">{plan.validity_text}</span></p>}
                    {plan.best_for && <p><span className="font-semibold text-slate-600">Best For:</span> <span className="text-slate-600">{plan.best_for}</span></p>}
                    {plan.investment_note && <p><span className="font-semibold text-slate-600">Investment Note:</span> <span className="text-slate-600">{plan.investment_note}</span></p>}
                    {plan.action_type && plan.action_type !== 'portal_flow' && (
                      <p><span className="font-semibold text-slate-600">CTA Routes To:</span> <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[11px] text-slate-700">{plan.action_url || '(not set)'}</code></p>
                    )}
                    {plan.benefit_categories?.length > 0 && (
                      <div className="pt-1">
                        <p className="font-semibold text-slate-600 mb-2">Benefits ({plan.benefit_categories.length} categor{plan.benefit_categories.length !== 1 ? 'ies' : 'y'}):</p>
                        {plan.benefit_categories.map((cat, ci) => (
                          <div key={ci} className="ml-1 mb-2">
                            <p className="font-semibold text-slate-700 text-xs">{cat.category_name}</p>
                            <ul className="ml-4 text-xs text-slate-600 space-y-0.5 mt-1">{cat.items?.map((item, ii) => <li key={ii} className="flex items-start"><CheckCircle2 className="w-3 h-3 text-emerald-500 mr-1 mt-0.5 flex-shrink-0" />{item}</li>)}</ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MembershipPlansPage;
